/**
 * MiniQuoteCart.tsx — Quote Cart Icon Block (Visual Editor version)
 *
 * Mirror of MiniCart but for the B2B quote cart.
 * Uses a document/quote icon instead of a shopping bag.
 * Wired to the quote store — shows real item count, opens quote drawer on click.
 *
 *2026-05-04 (fix ):
 *   Rewritten to use static imports + `useStore` from @nanostores/react
 *   instead of dynamic `import('@agentic/magento-client/stores/quote')` +
 *   manual `subscribe()`. Mirrors the working pattern in cart/MiniCart.tsx
 *   and QuoteDrawer.tsx. Eliminates any chunk-split / module-instance
 *   mismatch that was preventing `addToQuote()` calls in AddToCart.tsx
 *   from propagating to this component's count subscriber.
 */
import React, { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import {
  quoteItemCount,
  openQuoteCart,
  initQuoteCart,
} from '@agentic/magento-client/stores/quote';
import { isB2bQuoteEnabled } from '@agentic/magento-client/lib/feature-flags';

interface MiniQuoteCartProps {
  data: {
    showBadge?: boolean;
    [key: string]: unknown;
  };
  styles?: string;
}

export const MiniQuoteCart: React.FC<MiniQuoteCartProps> = ({ data, styles }) => {
  const { showBadge = true } = data ?? {};
  const itemCount = useStore(quoteItemCount);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    initQuoteCart();
  }, []);

  // Smart visibility: only render when the AgenticEcom_B2bSuite (Request-a-Quote)
  // module is installed + enabled, synced via global-data (SSR⇄client parity, no
  // extra Magento call). In the page editor we always show it so the merchant can
  // place/preview the block. After all hooks → React rules-of-hooks safe.
  const editorPreview = (data as { _editorPreview?: boolean })?._editorPreview === true;
  if (!editorPreview && !isB2bQuoteEnabled()) return null;

  const handleClick = () => {
    openQuoteCart();
  };

  interface MiniQuoteSettings {
    pillBackground?: string;
    pillBorder?: string;
    pillRadius?: string;
    pillPadding?: string;
    iconSize?: number;
    color?: string;
    variant?: string;
  }
  const settings = (typeof data?.settings === 'object' && data?.settings !== null
    ? (data.settings as MiniQuoteSettings)
    : ((data ?? {}) as MiniQuoteSettings));
  const {
    pillBackground = 'var(--color-header-icon-bg, var(--color-header-search-bg, rgba(255,255,255,0.06)))',
    pillBorder = '1px solid var(--color-header-icon-border, var(--color-header-search-border, var(--glass-border, rgba(255,255,255,0.18))))',
    pillRadius = 'var(--style-btn-radius, 9999px)',
    pillPadding = '4px 14px',
    iconSize,
    color = '',
    variant = 'icon-badge',
  } = settings;

  // expansion: real variants
  const isTextPill = variant === 'icon-text-pill';
  const isIconOnly = variant === 'icon-only';
  const buttonLabel: string = (data?.label as string | undefined) ?? ((data?.content as { label?: string } | undefined)?.label) ?? 'Quote';

  const pillStyle: React.CSSProperties = isTextPill ? {
    backgroundColor: pillBackground,
    border: pillBorder,
    borderRadius: pillRadius,
    padding: '8px 14px',
    minHeight: '36px',
    color: color || 'var(--color-text-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: 500,
  } : {
    // Resting bg via the --color-header-icon-bg VARIABLE (not direct inline
    // backgroundColor) so .header-icon-btn:hover can flip it to the shared
    // orange hover chip — matching the cart icon. A direct inline bg (1,0,0,0)
    // would beat the hover rule and freeze the resting fill. See WishlistIcon.
    ['--color-header-icon-bg' as string]: pillBackground,
    border: pillBorder,
    borderRadius: pillRadius,
    padding: pillPadding,
    minHeight: '32px',
    // Color is driven by .header-icon-btn class — DO NOT set inline `color` here,
    // inline style (1,0,0,0) beats the :hover rule (0,2,0) and freezes the icon.
    // Expose any user override as the --color-header-icon CSS variable instead.
    ...(color ? { ['--color-header-icon' as string]: color } : {}),
  };

  // Use 0 during SSR/hydration to match server HTML
  const displayCount = mounted ? itemCount : 0;

  return (
    <div className={`relative ${styles ?? ''}`}>
      <button
        onClick={handleClick}
        className="relative flex items-center justify-center transition-all duration-200 header-icon-btn"
        style={pillStyle}
        aria-label={`Quote cart with ${displayCount} items`}
        title="Request a Quote"
      >
        {/* Document/Quote icon — distinct from the shopping bag */}
        <svg
          style={{
            width: iconSize ? `${iconSize}px` : 'var(--style-header-icon-size, 22px)',
            height: iconSize ? `${iconSize}px` : 'var(--style-header-icon-size, 22px)',
          }}
          fill="none" viewBox="0 0 24 24" stroke="var(--icon-color, currentColor)"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        {isTextPill && (
          <span className="text-[13px] font-medium">
            {buttonLabel}{displayCount > 0 ? ` (${displayCount})` : ''}
          </span>
        )}
        {showBadge && !isIconOnly && !isTextPill && displayCount > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 font-extrabold text-[10px] rounded-full w-5 h-5 flex items-center justify-center border-2"
            style={{
              backgroundColor: 'var(--color-global-badge-bg, var(--accent-primary))',
              color: 'var(--color-global-badge-text, #ffffff)',
              borderColor: 'var(--surface-1, #06080e)',
            }}
          >
            {displayCount > 99 ? '99+' : displayCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default MiniQuoteCart;
